import { BlockEditor } from '@components/App/Modal/BlockModal'
import { useMediaQuery } from '@lib/hooks'
import { type Block, selectNodeBlocks } from '@octopus-do/octopus-project-core'
import { ThemeColors } from '@octopus-do/octopus-storybook'
import { BlockActions } from '@store/blockSaga'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import type { AppState } from '@store/types'
import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { DEFAULT_PAGE_NAME } from '@/constants/defaultNames'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useAuth } from '@/hooks'
import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import { buildRecipient, clsJoin, toLightThemeColor } from '@/utils'
import { getFrameViewIcon } from '@/utils/getFrameViewIcon'
import Comments from '../../../ArtBoard/CommentsLegacy'
import { Node } from '../PageContentView/components/Node'
import { AddBlock } from './components/AddBlock'
import { AddBlockWithBlocks } from './components/AddBlockWithBlocks'
import css from './PageContent.module.scss'

type Props = {
  nodeId: string
  withComments?: boolean
}

export const PageContent: React.FC<Props> = ({ nodeId, withComments }) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const node = useSelector(migrationProjectSelector.getProjectNodeById(nodeId))

  const blocks = useSelector<AppState, Block[]>((state) =>
    selectNodeBlocks(nodeId)({
      project: state.manager.project,
      relations: state.relations,
    })
  )

  const { title, colorId } = node
  const colorHex = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )
  const theme = useSelector(migrationProjectSelector.getProjectTheme)
  const normalizedColorHex = toLightThemeColor(colorHex, theme)

  const canEdit = useSelector(migrationProjectSelector.getProjectAccessCanEdit)
  const canEditContent = useSelector(
    migrationProjectSelector.getProjectAccessCanEditContent
  )

  const canWriteBlockContent = canEdit || canEditContent

  const canComment = useSelector(
    migrationProjectSelector.getProjectAccessCanComment
  )

  const isReadOnly = useSelector(
    migrationProjectSelector.getProjectLegacyAccessTypeIsReadOnly
  )

  const artboardDisabled = useArtBoardDisabled()

  // checked here for both buttons: AddBlockWithBlocks has no disabled state
  const handleAddBlock = () => {
    if (artboardDisabled) {
      return
    }

    sendGtmEvent(GtmTriggerName.BLOCK_ADD_BY_CONTENT)

    dispatch(BlockActions.addBlockByPageContent())
  }

  const lessThanDesktop = useMediaQuery('screen and (max-width: 720px)')

  const auth = useAuth()
  const author = buildRecipient(auth)

  return (
    <div className={css.page}>
      <div className={css.titleWrapper} style={{ color: normalizedColorHex }}>
        <div className={css.iconWrapper}>
          {getFrameViewIcon(node.variant, lessThanDesktop ? 16 : 32)}
        </div>
        <span className={css.title}>{title || DEFAULT_PAGE_NAME}</span>
      </div>

      <div className={css.container}>
        <div className={css.content}>
          {blocks.length ? (
            <>
              {blocks.map(({ id }) => (
                <div className={css.editor_container} key={id}>
                  <BlockEditor blockId={id} readOnly={!canWriteBlockContent} />
                </div>
              ))}

              {!isReadOnly && (
                <AddBlockWithBlocks onAddBlock={handleAddBlock} />
              )}
            </>
          ) : (
            <AddBlock
              isReadOnly={isReadOnly || artboardDisabled}
              onAddBlock={handleAddBlock}
            />
          )}
          {withComments && canComment && (
            <Comments author={author} commentable={node.id} />
          )}
        </div>
        <div className={css.sidebar}>
          <div className={clsJoin(css.node, ThemeColors.light)}>
            <Node name={title} color={normalizedColorHex} {...node} />
          </div>
        </div>
      </div>
    </div>
  )
}
